import { useState, useEffect } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { useGetMatterV2 } from '../../../api/queries/useGetMatterV2';
import { DocViewerFullFact } from '../doc-viewer/doc-viewer-full-fact';
import { FactDocViewer } from '../doc-viewer/fact-doc-viewer';
import { Icon } from '../icon';
import { CloseIcon } from '../pdf-viewer/components/close-icon';
import { Button } from '../shadcn/button';
import { useGetFactV2 } from '@/api/queries/useGetFactV2';
import { Drawer } from '@/new/components/drawers/drawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';

interface FactWithDocumentDrawerProps {
  factId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function FactWithDocumentDrawer({ factId, isOpen, onClose }: FactWithDocumentDrawerProps) {
  const { matterId, subsetId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);
  const requestSubsetId = subsetId ?? matter?.allDocumentsCaseId ?? undefined;

  const { data: fact, isLoading, error } = useGetFactV2(matterId, requestSubsetId, factId);
  const [isFactInfoExpanded, setIsFactInfoExpanded] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);

  const handleClose = () => {
    setIsFactInfoExpanded(false);
    setSelectedDocId(null);
    onClose();
  };

  const handleDocumentChange = (newDocId: string) => {
    if (newDocId !== selectedDocId) {
      setSelectedDocId(newDocId);
    }
  };

  // When a fact is loaded, automatically select the first document
  useEffect(() => {
    if (fact && fact.locations && fact.locations.length > 0) {
      const firstLocation = fact.locations[0];
      setSelectedDocId(firstLocation.docId);
    }
  }, [fact]);

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} hideCloseButton={true}>
      <div className="flex h-full flex-col">
        <CloseIcon onClick={handleClose} />
        {error ? (
          <div className="my-10 flex h-full w-full flex-col items-center justify-center text-red-600">
            <Icon className="lucide--warning" />
            <div className="font-semibold">Sorry, there was a problem loading this fact.</div>
            <div className="mt-2 text-xs text-gray-500">Please try again or contact support if the issue persists.</div>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="my-20 flex h-full w-full items-center justify-center">
                <Icon className="lucide--loader-2 animate-spin duration-700" />
              </div>
            )}
            {fact && (
              <>
                {/* Fact Information Section */}
                <div className="border-b border-gray-300 bg-gray-50">
                  <div className="flex items-center gap-3 px-4 py-2">
                    <Button onClick={() => setIsFactInfoExpanded(!isFactInfoExpanded)} size="sm" className="h-9 w-32">
                      {isFactInfoExpanded ? 'Hide full fact' : 'Show full fact'}
                      <motion.div
                        animate={{ rotate: isFactInfoExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                      >
                        <Icon className="lucide--chevron-down" />
                      </motion.div>
                    </Button>
                    {fact.locations && fact.locations.length > 0 && (
                      <div className="flex h-9 items-center gap-2 rounded-md border border-gray-200 bg-white px-3">
                        <div className="flex items-center gap-1.5">
                          <Icon className="lucide--file-text text-blue-600" />
                          <span className="text-xs font-medium text-gray-700">Document:</span>
                        </div>
                        {fact.locations.length === 1 ? (
                          <div className="flex h-6 items-center">
                            <span className="max-w-52 truncate text-xs text-gray-700">
                              {fact.locations[0].doc?.file?.name || 'Document'}
                            </span>
                          </div>
                        ) : (
                          <Select value={selectedDocId || ''} onValueChange={handleDocumentChange}>
                            <SelectTrigger className="h-6 w-32 bg-white text-xs shadow-sm transition-colors hover:bg-gray-50">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {fact.locations.map((location) => (
                                <SelectItem key={location.docId} value={location.docId}>
                                  <div className="flex items-center gap-2">
                                    <span className="truncate">{location.doc?.file?.name || 'Document'}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                  <AnimatePresence>
                    {isFactInfoExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="px-4 pb-4">
                          <DocViewerFullFact
                            fact={fact}
                            subsetId={subsetId ?? ''}
                            showComments={false}
                            showDocumentDetails={false}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Document Viewer Section */}
                <div className="flex-1 overflow-hidden">
                  <AnimatePresence mode="wait">
                    {selectedDocId ? (
                      <motion.div
                        key={selectedDocId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="h-full"
                      >
                        <FactDocViewer
                          docId={selectedDocId}
                          factId={factId}
                          hideFeatureButtons={false}
                          hideCloseButton={true}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex h-full items-center justify-center text-gray-500"
                      >
                        <p>Select a document to view</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
